/* eslint-disable i18next/no-literal-string --
 * Seed data, not UI copy. Category names are persisted and matched by name
 * when seeding, and `icon` is a `lucide-react` component name.
 */
import type { LucideIcon } from 'lucide-react';
import { resolveCategoryIcon } from './categoryIcons';

/**
 * A category that ships with the product.
 *
 * `icon` is a `lucide-react` component NAME, not a component, because that is
 * what the backend persists — keeping the shipped definition in the same
 * currency as the database means seeding a starter category is a plain POST of
 * this record, with no translation step to get wrong.
 */
export interface StarterCategory {
  name: string;
  /** A key of `CATEGORY_ICON_REGISTRY`. */
  icon: string;
  /** Position in the "Add field" picker rail. */
  sortOrder: number;
}

/**
 * The categories the standard field library is organised into.
 *
 * Order and names mirror `STANDARD_FIELD_LIBRARY` — the shipped fields declare
 * these same five categories, so the two lists have to agree or a seeded
 * category and the fields that belong to it end up in different groups. This is
 * the source of truth for both; the field catalog derives its rail icons from
 * here rather than repeating them.
 */
export const STARTER_CATEGORIES: readonly StarterCategory[] = [
  { name: 'Geography', icon: 'Globe', sortOrder: 0 },
  { name: 'Media buy', icon: 'ShoppingCart', sortOrder: 1 },
  { name: 'Campaign', icon: 'Flag', sortOrder: 2 },
  { name: 'Creative', icon: 'Palette', sortOrder: 3 },
  { name: 'Targeting & offer', icon: 'Target', sortOrder: 4 },
];

/**
 * Where fields land when the category holding them is deleted.
 *
 * Not part of `STARTER_CATEGORIES` — it isn't one of the shipped five and the
 * "Add standard categories" action must not create it — but it is a name the
 * product creates, so it gets a proper icon like the rest. Mirrors
 * `UNCATEGORIZED_CATEGORY` in the assets-api standard-category catalog.
 */
export const UNCATEGORIZED_CATEGORY: StarterCategory = {
  name: 'Uncategorized',
  icon: 'Boxes',
  sortOrder: 999,
};

/** Case-insensitive, matching how the backend compares category names. */
const STARTER_ICON_BY_NAME = new Map(
  [...STARTER_CATEGORIES, UNCATEGORIZED_CATEGORY].map(
    (c) => [c.name.toLowerCase(), c.icon] as const,
  ),
);

/** The shipped icon name for a category we ship, or undefined for a custom one. */
export function starterCategoryIconName(name: string | null | undefined): string | undefined {
  return name ? STARTER_ICON_BY_NAME.get(name.trim().toLowerCase()) : undefined;
}

/** Starter categories as components, for the "Add field" picker rail. */
export const STARTER_CATEGORY_ICON: Record<string, LucideIcon> = Object.fromEntries(
  STARTER_CATEGORIES.map((c) => [c.name, resolveCategoryIcon(c.icon)]),
);

/**
 * The icon to render for a category.
 *
 * Three tiers, in order: an icon the org explicitly chose, the shipped icon for
 * a starter category, then the generic default. The middle tier is what stops
 * the starter categories from all looking alike — rows created by the backfill
 * carry no icon of their own, and a name we ship is enough to know which glyph
 * belongs to it.
 */
export function categoryIconFor(
  name: string | null | undefined,
  storedIcon: string | null | undefined,
): LucideIcon {
  return resolveCategoryIcon(storedIcon ?? starterCategoryIconName(name));
}
